import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './stores/auth';
import { AppLayout } from './components/AppLayout';
import { RequirePermission } from './components/RequirePermission';
import LoginPage from './pages/Login';

// Public marketing site
import SitePage from './pages/site/Home';
import SiteListings from './pages/site/Listings';
import SiteListingDetail from './pages/site/ListingDetail';
import SiteApply from './pages/site/Apply';
import SiteAbout from './pages/site/About';
import SiteLivingWithUs from './pages/site/LivingWithUs';
import SiteFaqs from './pages/site/Faqs';
import SiteContact from './pages/site/Contact';
import { LotusMark } from './components/site/Logo';

// Authenticated app
import HomePage from './pages/Home';
import PropertiesPage from './pages/Properties';
import PropertyDetailPage from './pages/PropertyDetail';
import UnitsPage from './pages/Units';
import ListingsPage from './pages/Listings';
import LeadsPage from './pages/Leads';
import ViewingsPage from './pages/Viewings';
import ApplicationsPage from './pages/Applications';
import TenanciesPage from './pages/Tenancies';
import TenancyDetailPage from './pages/TenancyDetail';
import DepositsPage from './pages/Deposits';
import ResidentsPage from './pages/Residents';
import WorkOrdersPage from './pages/WorkOrders';
import WorkOrderDetailPage from './pages/WorkOrderDetail';
import AssetsPage from './pages/Assets';
import CompliancePage from './pages/Compliance';
import VendorsPage from './pages/Vendors';
import InspectionsPage from './pages/Inspections';
import InvoicesPage from './pages/Invoices';
import NotesPage from './pages/Notes';
import BillsPage from './pages/Bills';
import PaymentsPage from './pages/Payments';
import RecurringPage from './pages/Recurring';
import BankingPage from './pages/Banking';
import FixedAssetsPage from './pages/FixedAssets';
import BudgetsPage from './pages/Budgets';
import VatReturnsPage from './pages/VatReturns';
import CisPage from './pages/Cis';
import FinanceSettingsPage from './pages/FinanceSettings';
import AccountsPage from './pages/Accounts';
import JournalsPage from './pages/Journals';
import PeriodsPage from './pages/Periods';
import StatementsPage from './pages/Statements';
import ReportsPage from './pages/Reports';
import UsersPage from './pages/Users';
import RolesPage from './pages/Roles';
import EntitiesPage from './pages/Entities';
import AuditPage from './pages/Audit';
import MyHomePage from './pages/resident/MyHome';

/** /app/dashboards/:id was the old detail route; it is now a view on the home screen. */
function LegacyDashboardRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/app/v/${id}`} replace />;
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-ink-50">
        <LotusMark className="h-9 w-9 animate-pulse" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-400">Lotus Living</span>
      </div>
    );
  }
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}

export default function App() {
  const restore = useAuth((s) => s.restore);
  useEffect(() => { void restore(); }, [restore]);

  return (
    <Routes>
      {/* Public marketing website — no authentication */}
      <Route path="/" element={<SitePage />} />
      <Route path="/rent" element={<SiteListings />} />
      <Route path="/rent/:slug" element={<SiteListingDetail />} />
      <Route path="/rent/:slug/apply" element={<SiteApply />} />
      <Route path="/locations" element={<Navigate to="/rent" replace />} />
      <Route path="/about" element={<SiteAbout />} />
      <Route path="/living-with-us" element={<SiteLivingWithUs />} />
      <Route path="/faqs" element={<SiteFaqs />} />
      <Route path="/contact" element={<SiteContact />} />
      <Route path="/login" element={<LoginPage />} />

      {/* One authenticated application for every role */}
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        {/* One home screen: the built-in overview and every saved view live here. */}
        <Route index element={<HomePage />} />
        <Route path="overview" element={<HomePage overview />} />
        <Route path="v/:id" element={<RequirePermission page="dashboards"><HomePage /></RequirePermission>} />
        <Route path="my-home" element={<MyHomePage />} />

        {/* The dashboards used to be their own section; keep old links working. */}
        <Route path="dashboards" element={<Navigate to="/app" replace />} />
        <Route path="dashboards/:id" element={<LegacyDashboardRedirect />} />

        <Route path="properties" element={<RequirePermission page="properties"><PropertiesPage /></RequirePermission>} />
        <Route path="properties/:id" element={<RequirePermission page="properties"><PropertyDetailPage /></RequirePermission>} />
        <Route path="units" element={<RequirePermission page="units"><UnitsPage /></RequirePermission>} />
        <Route path="listings" element={<RequirePermission page="listings"><ListingsPage /></RequirePermission>} />

        <Route path="leads" element={<RequirePermission page="leads"><LeadsPage /></RequirePermission>} />
        <Route path="viewings" element={<RequirePermission page="viewings"><ViewingsPage /></RequirePermission>} />
        <Route path="applications" element={<RequirePermission page="applications"><ApplicationsPage /></RequirePermission>} />
        <Route path="tenancies" element={<RequirePermission page="tenancies"><TenanciesPage /></RequirePermission>} />
        <Route path="tenancies/:id" element={<RequirePermission page="tenancies"><TenancyDetailPage /></RequirePermission>} />
        <Route path="deposits" element={<RequirePermission page="deposits"><DepositsPage /></RequirePermission>} />
        <Route path="residents" element={<RequirePermission page="residents"><ResidentsPage /></RequirePermission>} />

        <Route path="work-orders" element={<RequirePermission page="workOrders"><WorkOrdersPage /></RequirePermission>} />
        <Route path="work-orders/:id" element={<RequirePermission page="workOrders"><WorkOrderDetailPage /></RequirePermission>} />
        <Route path="assets" element={<RequirePermission page="assets"><AssetsPage /></RequirePermission>} />
        <Route path="compliance" element={<RequirePermission page="compliance"><CompliancePage /></RequirePermission>} />
        <Route path="vendors" element={<RequirePermission page="vendors"><VendorsPage /></RequirePermission>} />
        <Route path="inspections" element={<RequirePermission page="inspections"><InspectionsPage /></RequirePermission>} />

        <Route path="invoices" element={<RequirePermission page="invoices"><InvoicesPage /></RequirePermission>} />
        <Route path="notes" element={<RequirePermission page="creditDebitNotes"><NotesPage /></RequirePermission>} />
        <Route path="bills" element={<RequirePermission page="bills"><BillsPage /></RequirePermission>} />
        <Route path="payments" element={<RequirePermission page="payments"><PaymentsPage /></RequirePermission>} />
        <Route path="recurring" element={<RequirePermission page="recurringInvoices"><RecurringPage /></RequirePermission>} />
        <Route path="banking" element={<RequirePermission page="banking"><BankingPage /></RequirePermission>} />
        <Route path="accounts" element={<RequirePermission page="chartOfAccounts"><AccountsPage /></RequirePermission>} />
        <Route path="journals" element={<RequirePermission page="journals"><JournalsPage /></RequirePermission>} />
        <Route path="fixed-assets" element={<RequirePermission page="fixedAssets"><FixedAssetsPage /></RequirePermission>} />
        <Route path="budgets" element={<RequirePermission page="budgets"><BudgetsPage /></RequirePermission>} />
        <Route path="periods" element={<RequirePermission page="periods"><PeriodsPage /></RequirePermission>} />
        <Route path="vat" element={<RequirePermission page="taxReports"><VatReturnsPage /></RequirePermission>} />
        <Route path="cis" element={<RequirePermission page="cis"><CisPage /></RequirePermission>} />
        <Route path="statements" element={<RequirePermission page="glReports"><StatementsPage /></RequirePermission>} />
        <Route path="finance-settings" element={<RequirePermission page="financeSettings"><FinanceSettingsPage /></RequirePermission>} />

        <Route path="reports" element={<RequirePermission page="reports"><ReportsPage /></RequirePermission>} />

        <Route path="users" element={<RequirePermission page="users"><UsersPage /></RequirePermission>} />
        <Route path="roles" element={<RequirePermission page="roles"><RolesPage /></RequirePermission>} />
        <Route path="entities" element={<RequirePermission page="entities"><EntitiesPage /></RequirePermission>} />
        <Route path="audit" element={<RequirePermission page="auditLog"><AuditPage /></RequirePermission>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
